// config/formTypes.js
export const FIELD_TYPES = {
  TEXT: "text",
  TEXTAREA: "textarea",
  EMAIL: "email",
  NUMBER: "number",
  PHONE: "phone",
  DATE: "date",
  TIME: "time",
  SELECT: "select",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  MULTIPLE_CHOICE: "multiple_choice",
  FILE: "file",
  RATING: "rating",
  SCALE: "scale",
  YES_NO: "yes_no",
  URL: "url",
};

export const FIELD_ICONS = {
  [FIELD_TYPES.TEXT]: "📝",
  [FIELD_TYPES.TEXTAREA]: "📄",
  [FIELD_TYPES.EMAIL]: "📧",
  [FIELD_TYPES.NUMBER]: "🔢",
  [FIELD_TYPES.PHONE]: "📱",
  [FIELD_TYPES.DATE]: "📅",
  [FIELD_TYPES.TIME]: "⏰",
  [FIELD_TYPES.SELECT]: "📋",
  [FIELD_TYPES.RADIO]: "⚪",
  [FIELD_TYPES.CHECKBOX]: "☑️",
  [FIELD_TYPES.MULTIPLE_CHOICE]: "📊",
  [FIELD_TYPES.FILE]: "📁",
  [FIELD_TYPES.RATING]: "⭐",
  [FIELD_TYPES.SCALE]: "📏",
  [FIELD_TYPES.YES_NO]: "❓",
  [FIELD_TYPES.URL]: "🔗",
};

export const FIELD_NAMES = {
  [FIELD_TYPES.TEXT]: "Texto corto",
  [FIELD_TYPES.TEXTAREA]: "Texto largo",
  [FIELD_TYPES.EMAIL]: "Email",
  [FIELD_TYPES.NUMBER]: "Número",
  [FIELD_TYPES.PHONE]: "Teléfono",
  [FIELD_TYPES.DATE]: "Fecha",
  [FIELD_TYPES.TIME]: "Hora",
  [FIELD_TYPES.SELECT]: "Lista desplegable",
  [FIELD_TYPES.RADIO]: "Opción múltiple",
  [FIELD_TYPES.CHECKBOX]: "Casillas de verificación",
  [FIELD_TYPES.MULTIPLE_CHOICE]: "Selección múltiple",
  [FIELD_TYPES.FILE]: "Subir archivo",
  [FIELD_TYPES.RATING]: "Calificación",
  [FIELD_TYPES.SCALE]: "Escala lineal",
  [FIELD_TYPES.YES_NO]: "Sí/No",
  [FIELD_TYPES.URL]: "URL",
};

// Estructura base de un formulario
export const FORM_TEMPLATE = {
  id: "",
  title: "Formulario sin título",
  description: "",
  settings: {
    isPublic: true,
    allowAnonymous: true,
    requireLogin: false,
    collectEmails: false,
    limitResponses: false,
    maxResponses: null,
    allowMultipleResponses: false,
    showProgressBar: true,
    shuffleQuestions: false,
    confirmationMessage: "¡Gracias por tu respuesta!",
    redirectUrl: null,
  },
  styling: {
    theme: "default",
    backgroundColor: "#ffffff",
    primaryColor: "#14840c",
    fontFamily: "Inter",
    headerImage: null,
  },
  fields: [],
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  status: "draft", // draft, published, closed
  responseCount: 0,
};

// Estructura base de un campo
export const FIELD_TEMPLATE = {
  id: "",
  type: FIELD_TYPES.TEXT,
  title: "",
  description: "",
  placeholder: "",
  required: false,
  order: 0,
  validation: {
    minLength: null,
    maxLength: null,
    min: null,
    max: null,
    pattern: null,
    customValidation: null,
  },
  options: [], // Para select, radio, checkbox, etc.
  settings: {
    allowOther: false,
    randomizeOptions: false,
    multipleSelection: false,
    showAsGrid: false,
    columns: 1,
  },
};

// Opciones predefinidas para diferentes tipos de campos
export const PREDEFINED_OPTIONS = {
  YES_NO: [
    { id: "yes", label: "Sí", value: "yes" },
    { id: "no", label: "No", value: "no" },
  ],
  RATING: [
    { id: "1", label: "1 - Muy malo", value: 1 },
    { id: "2", label: "2 - Malo", value: 2 },
    { id: "3", label: "3 - Regular", value: 3 },
    { id: "4", label: "4 - Bueno", value: 4 },
    { id: "5", label: "5 - Excelente", value: 5 },
  ],
  SATISFACTION: [
    {
      id: "very_unsatisfied",
      label: "Muy insatisfecho",
      value: "very_unsatisfied",
    },
    { id: "unsatisfied", label: "Insatisfecho", value: "unsatisfied" },
    { id: "neutral", label: "Neutral", value: "neutral" },
    { id: "satisfied", label: "Satisfecho", value: "satisfied" },
    { id: "very_satisfied", label: "Muy satisfecho", value: "very_satisfied" },
  ],
  FREQUENCY: [
    { id: "never", label: "Nunca", value: "never" },
    { id: "rarely", label: "Rara vez", value: "rarely" },
    { id: "sometimes", label: "Algunas veces", value: "sometimes" },
    { id: "often", label: "A menudo", value: "often" },
    { id: "always", label: "Siempre", value: "always" },
  ],
  AGREEMENT: [
    {
      id: "strongly_disagree",
      label: "Totalmente en desacuerdo",
      value: "strongly_disagree",
    },
    { id: "disagree", label: "En desacuerdo", value: "disagree" },
    { id: "neutral", label: "Neutral", value: "neutral" },
    { id: "agree", label: "De acuerdo", value: "agree" },
    {
      id: "strongly_agree",
      label: "Totalmente de acuerdo",
      value: "strongly_agree",
    },
  ],
  PRIORITY: [
    { id: "very_low", label: "Muy baja", value: "very_low" },
    { id: "low", label: "Baja", value: "low" },
    { id: "medium", label: "Media", value: "medium" },
    { id: "high", label: "Alta", value: "high" },
    { id: "very_high", label: "Muy alta", value: "very_high" },
  ],
};

// Validaciones por tipo de campo
export const FIELD_VALIDATIONS = {
  [FIELD_TYPES.EMAIL]: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Ingrese un email válido",
  },
  [FIELD_TYPES.PHONE]: {
    pattern: /^[\+]?[1-9][\d]{0,15}$/,
    message: "Ingrese un teléfono válido",
  },
  [FIELD_TYPES.URL]: {
    pattern: /^https?:\/\/.+\..+/,
    message: "Ingrese una URL válida",
  },
  [FIELD_TYPES.NUMBER]: {
    pattern: /^\d+$/,
    message: "Ingrese solo números",
  },
};

// Temas disponibles para formularios
export const FORM_THEMES = {
  DEFAULT: {
    id: "default",
    name: "Por defecto",
    primaryColor: "#14840c",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderColor: "#d1d5db",
  },
  BLUE: {
    id: "blue",
    name: "Azul",
    primaryColor: "#3b82f6",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderColor: "#d1d5db",
  },
  PURPLE: {
    id: "purple",
    name: "Morado",
    primaryColor: "#8b5cf6",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderColor: "#d1d5db",
  },
  DARK: {
    id: "dark",
    name: "Oscuro",
    primaryColor: "#10b981",
    backgroundColor: "#1f2937",
    textColor: "#ffffff",
    borderColor: "#374151",
  },
  MINIMAL: {
    id: "minimal",
    name: "Minimal",
    primaryColor: "#6b7280",
    backgroundColor: "#f9fafb",
    textColor: "#374151",
    borderColor: "#e5e7eb",
  },
};

// Tipos de archivo permitidos
export const ALLOWED_FILE_TYPES = {
  IMAGE: {
    types: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    maxSize: 5 * 1024 * 1024, // 5MB
  },
  DOCUMENT: {
    types: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    extensions: [".pdf", ".doc", ".docx"],
    maxSize: 10 * 1024 * 1024, // 10MB
  },
  SPREADSHEET: {
    types: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    extensions: [".xls", ".xlsx"],
    maxSize: 10 * 1024 * 1024, // 10MB
  },
  ANY: {
    types: ["*/*"],
    extensions: ["*"],
    maxSize: 50 * 1024 * 1024, // 50MB
  },
};

// Estados posibles de formularios
export const FORM_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  CLOSED: "closed",
  ARCHIVED: "archived",
};

// Configuraciones de exportación
export const EXPORT_FORMATS = {
  CSV: "csv",
  EXCEL: "excel",
  JSON: "json",
  PDF: "pdf",
};

// Tipos de análisis disponibles
export const ANALYTICS_TYPES = {
  RESPONSE_COUNT: "response_count",
  COMPLETION_RATE: "completion_rate",
  FIELD_ANALYSIS: "field_analysis",
  TIME_ANALYSIS: "time_analysis",
  DEMOGRAPHICS: "demographics",
};

// Funciones de utilidad
export const getFieldIcon = (fieldType) => {
  return FIELD_ICONS[fieldType] || "❓";
};

export const getFieldName = (fieldType) => {
  return FIELD_NAMES[fieldType] || "Campo desconocido";
};

export const validateFieldValue = (field, value) => {
  const errors = [];

  // Campo requerido
  if (
    field.required &&
    (!value || (typeof value === "string" && !value.trim()))
  ) {
    errors.push(`${field.title} es obligatorio`);
    return errors;
  }

  // Si el campo está vacío y no es requerido, no validar
  if (!value || (typeof value === "string" && !value.trim())) {
    return errors;
  }

  // Validaciones por tipo de campo
  const validation = FIELD_VALIDATIONS[field.type];
  if (validation && validation.pattern && !validation.pattern.test(value)) {
    errors.push(validation.message);
  }

  // Validaciones de longitud
  if (field.validation) {
    if (
      field.validation.minLength &&
      value.length < field.validation.minLength
    ) {
      errors.push(`Mínimo ${field.validation.minLength} caracteres`);
    }
    if (
      field.validation.maxLength &&
      value.length > field.validation.maxLength
    ) {
      errors.push(`Máximo ${field.validation.maxLength} caracteres`);
    }
  }

  // Validaciones numéricas
  if (field.type === FIELD_TYPES.NUMBER && field.validation) {
    const numValue = parseFloat(value);
    if (field.validation.min !== null && numValue < field.validation.min) {
      errors.push(`Valor mínimo: ${field.validation.min}`);
    }
    if (field.validation.max !== null && numValue > field.validation.max) {
      errors.push(`Valor máximo: ${field.validation.max}`);
    }
  }

  return errors;
};

export const generateFormUrl = (formId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/form/${formId}`;
};

export const generateEmbedCode = (formId) => {
  const formUrl = generateFormUrl(formId);
  return `<iframe src="${formUrl}" width="100%" height="600" frameborder="0"></iframe>`;
};

export const calculateFormCompletionTime = (fields) => {
  // Estimación basada en tipo de campo (en segundos)
  const timeEstimates = {
    [FIELD_TYPES.TEXT]: 10,
    [FIELD_TYPES.TEXTAREA]: 30,
    [FIELD_TYPES.EMAIL]: 8,
    [FIELD_TYPES.NUMBER]: 5,
    [FIELD_TYPES.PHONE]: 8,
    [FIELD_TYPES.DATE]: 5,
    [FIELD_TYPES.TIME]: 5,
    [FIELD_TYPES.SELECT]: 5,
    [FIELD_TYPES.RADIO]: 8,
    [FIELD_TYPES.CHECKBOX]: 10,
    [FIELD_TYPES.YES_NO]: 3,
    [FIELD_TYPES.RATING]: 5,
    [FIELD_TYPES.SCALE]: 5,
    [FIELD_TYPES.FILE]: 60,
    [FIELD_TYPES.URL]: 15,
  };

  const totalSeconds = fields.reduce((total, field) => {
    return total + (timeEstimates[field.type] || 10);
  }, 0);

  const minutes = Math.ceil(totalSeconds / 60);
  return `${minutes} min${minutes > 1 ? "s" : ""}`;
};
